import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../api/axios';
import { getAuthUser } from '../utils/auth';

const roleOptions = ['ADMIN', 'HR', 'EMPLOYEE'];

export default function UserManageForm({ profileMode = false }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const authUser = getAuthUser();
  const isEmployee = authUser?.role === 'EMPLOYEE';
  const resolvedId = profileMode ? authUser?.userId : id;
  const isEdit = Boolean(resolvedId);
  const [form, setForm] = useState({
    nombreCompleto: '',
    usuario: '',
    email: '',
    password: '',
    role: 'EMPLOYEE',
    employeeId: '',
  });
  const [isLoading, setIsLoading] = useState(isEdit);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isEmployeesLoading, setIsEmployeesLoading] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState('');

  useEffect(() => {
    const loadUser = async () => {
      if (profileMode && !resolvedId) {
        setError('No se pudo identificar el usuario.');
        setIsLoading(false);
        return;
      }
      if (!isEdit) {
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const response = await axios.get(`/users/${resolvedId}`);
        const user = response.data;
        setForm({
          nombreCompleto: user.nombreCompleto || '',
          usuario: user.usuario || '',
          email: user.email || '',
          password: '',
          role: user.role || 'EMPLOYEE',
          employeeId: user.employeeId ?? '',
        });
      } catch (err) {
        const message = err.response?.data?.message || 'No se pudo cargar el usuario.';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    loadUser();
  }, [resolvedId, isEdit, profileMode]);

  const loadEmployees = async () => {
    if (isEmployee) {
      return;
    }
    setIsEmployeesLoading(true);
    setError(null);
    try {
      const response = await axios.get('/employees');
      setEmployees(response.data);
    } catch (err) {
      const message = err.response?.data?.message || 'No se pudieron cargar los empleados.';
      setError(message);
    } finally {
      setIsEmployeesLoading(false);
    }
  };

  const openEmployeePicker = async () => {
    if (employees.length === 0) {
      await loadEmployees();
    }
    setIsPickerOpen(true);
  };

  const closeEmployeePicker = () => {
    setIsPickerOpen(false);
    setEmployeeSearch('');
  };

  const handleSelectEmployee = (employeeId) => {
    setForm((prev) => ({ ...prev, employeeId: employeeId.toString() }));
    closeEmployeePicker();
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setIsSaving(true);

    const payload = {
      nombreCompleto: form.nombreCompleto.trim(),
      usuario: form.usuario.trim(),
      email: form.email.trim(),
    };

    if (!isEmployee) {
      payload.role = form.role;
      payload.employeeId = form.employeeId ? Number(form.employeeId) : undefined;
    }

    if (!isEdit || form.password.trim()) {
      payload.password = form.password;
    }

    try {
      if (isEdit) {
        await axios.put(`/users/${resolvedId}`, payload);
      } else {
        await axios.post('/users', payload);
      }
      navigate(profileMode ? '/home/asistencia' : '/home/usuarios');
    } catch (err) {
      const message =
        err.response?.data?.message || 'No se pudo guardar el usuario. Verifica los datos.';
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const filteredEmployees = useMemo(() => {
    const query = employeeSearch.trim().toLowerCase();
    if (!query) {
      return employees;
    }
    return employees.filter((employee) => {
      const fullName = `${employee.firstName} ${employee.lastName}`.toLowerCase();
      return (
        fullName.includes(query) ||
        (employee.employeeCode || '').toLowerCase().includes(query)
      );
    });
  }, [employees, employeeSearch]);

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">
          {isEdit ? 'Editar usuario' : 'Crear usuario'}
        </h1>
        <p className="text-sm text-gray-500">
          {isEdit ? 'Actualiza los datos del usuario.' : 'Registro manual de usuarios del sistema.'}
        </p>
      </div>

      <div className="rounded-lg bg-white p-6 shadow-sm">
        {isLoading ? (
          <div className="text-sm text-gray-500">Cargando usuario...</div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-semibold text-gray-600">Nombre completo</label>
                <input
                  type="text"
                  name="nombreCompleto"
                  value={form.nombreCompleto}
                  onChange={handleChange}
                  required
                  className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-600">Usuario</label>
                <input
                  type="text"
                  name="usuario"
                  value={form.usuario}
                  onChange={handleChange}
                  required
                  className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-600">Correo</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-600">
                  Contrasena {isEdit ? '(opcional)' : ''}
                </label>
                <input
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  required={!isEdit}
                  className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                />
              </div>
              {!isEmployee && (
                <>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Rol</label>
                    <select
                      name="role"
                      value={form.role}
                      onChange={handleChange}
                      className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                    >
                      {roleOptions.map((role) => (
                        <option key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Empleado ID (opcional)</label>
                    <div className="mt-1 flex gap-2">
                      <input
                        type="number"
                        name="employeeId"
                        value={form.employeeId}
                        onChange={handleChange}
                        className="w-full rounded-lg border border-gray-200 p-2"
                        placeholder="Dejar vacio si no aplica"
                      />
                      <button
                        type="button"
                        onClick={openEmployeePicker}
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        Buscar
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {error && <div className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600">{error}</div>}

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={isSaving}
                className="rounded-lg bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700 disabled:opacity-60"
              >
                {isSaving ? 'Guardando...' : 'Guardar'}
              </button>
              <button
                type="button"
                onClick={() => navigate(profileMode ? '/home/asistencia' : '/home/usuarios')}
                className="rounded-lg border border-gray-200 px-4 py-2 text-gray-600 hover:bg-gray-50"
              >
                Cancelar
              </button>
            </div>
          </form>
        )}
      </div>

      {isPickerOpen && !isEmployee && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl rounded-lg bg-white shadow-lg">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">Seleccionar empleado</h2>
                <p className="text-xs text-gray-500">Elige el empleado para vincular.</p>
              </div>
              <button
                type="button"
                onClick={closeEmployeePicker}
                className="rounded-lg px-2 py-1 text-sm text-gray-500 hover:bg-gray-100"
              >
                Cerrar
              </button>
            </div>
            <div className="px-6 py-4">
              <input
                type="text"
                value={employeeSearch}
                onChange={(event) => setEmployeeSearch(event.target.value)}
                placeholder="Buscar por nombre o codigo"
                className="w-full rounded-lg border border-gray-200 p-2"
              />
            </div>
            <div className="max-h-80 overflow-y-auto px-6 pb-6">
              {isEmployeesLoading ? (
                <div className="text-sm text-gray-500">Cargando empleados...</div>
              ) : filteredEmployees.length === 0 ? (
                <div className="text-sm text-gray-500">No hay empleados que coincidan.</div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                        Nombre
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                        Codigo
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                        Accion
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredEmployees.map((employee) => (
                      <tr key={employee.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-700">
                          {employee.firstName} {employee.lastName}
                        </td>
                        <td className="px-3 py-2 text-gray-500">{employee.employeeCode}</td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() => handleSelectEmployee(employee.id)}
                            className="rounded bg-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-blue-700"
                          >
                            Seleccionar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
